import { useEffect } from 'react';
import { addDays, isBefore } from 'date-fns';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikRangeDatePicker } from '@/components/form/fields/FormikRangeDatePicker/FormikRangeDatePicker';
import { FilterListClearAll } from '@/bundle/_Opco/_BilledEventsPage/ui/FilterList/styles';
import { ChipList } from '@/components/Chip/ChipList/ChipList';
import { CHIP_TYPE_MAP, FiltersChipType, getChipsFromFilters } from '@/components/Chip/ChipList/helpers';
import { BilledEventsFiltersType } from '@/bundle/_Opco/_BilledEventsPage/hooks/useBilledEventsFilters/useBilledEventsFilters';

type FilterListPropsType = {
  filters: BilledEventsFiltersType;
  onApplyFilters: (filters: BilledEventsFiltersType) => void;
  onClearFilters: () => void;
};

export const BillingEventsFilterList = ({ filters, onApplyFilters, onClearFilters }: FilterListPropsType) => {
  const chips = getChipsFromFilters(filters);

  const formik = useFormik<BilledEventsFiltersType>({
    initialValues: filters,
    enableReinitialize: true,
    onSubmit: onApplyFilters,
  });

  const removeChip = (chip: FiltersChipType) => {
    if (!chip.removable) return;

    if (chip.type === CHIP_TYPE_MAP.transaction) {
      onApplyFilters({ ...filters, transactionName: '' });

      return;
    }
  };

  useEffect(() => {
    const isStartDateBeforeEndDate = isBefore(formik.values.dateStart, formik.values.dateEnd);

    if (isStartDateBeforeEndDate) return;

    // Add one day to start date when start date was selected after end date
    const endDate = addDays(formik.values.dateStart, 1);

    formik.setFieldValue('dateEnd', endDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.dateStart]);

  return (
    <Box mobile={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
      <FormikForm value={formik}>
        <Box justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' mobile={{ width: '100%' }}>
          <Box display='flex' columnGap={8} rowGap={8} flexWrap='wrap' mobile={{ width: '100%' }}>
            <FormikRangeDatePicker
              nameStart='dateStart'
              nameEnd='dateEnd'
              dateStart={formik.values.dateStart}
              dateEnd={formik.values.dateEnd}
            />
            <Box width={200} mobile={{ width: '100%' }}>
              <FormikInput name='transactionName' placeholder='Transaction Name' variant='small' />
            </Box>
          </Box>

          <Box alignItems='center' columnGap={10} mobile={{ width: '100%', justifyContent: 'end' }}>
            <FilterListClearAll type='button' onClick={onClearFilters}>
              Clear all
            </FilterListClearAll>
            <Button width={140} size='medium' type='submit' variant='secondary'>
              Apply
            </Button>
          </Box>
        </Box>
      </FormikForm>
      <Box marginBottom='8px'>
        <ChipList chips={chips} onRemoveChip={removeChip} />
      </Box>
    </Box>
  );
};
